import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import {
  editLeagueUpdate,
  fetchAdminHomepageUpdate,
  fetchAdminLeagueUpdates,
  publishLeagueUpdate,
  removeHomepageUpdate,
  uploadUpdateFile,
  uploadUpdateImage,
} from '../services/league'
import type { LeagueUpdate } from '../types'
import { Card, EmptyState, Loading, Notice, formatDate, formatPostedDate } from './ui'
import UpdateBody from './UpdateBody'
import './UpdatesPanel.css'

/**
 * Admin control for league updates: post a new one, edit an existing one,
 * take the current one off the homepage, and see the recent archive.
 */
export default function UpdatesPanel() {
  const homepage = useAsync(fetchAdminHomepageUpdate, [])
  const archive = useAsync(fetchAdminLeagueUpdates, [])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [editing, setEditing] = useState<LeagueUpdate | null>(null)
  const [busy, setBusy] = useState(false)
  const [attaching, setAttaching] = useState(false)
  const [status, setStatus] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const bodyField = useRef<HTMLTextAreaElement>(null)

  const blocked = busy || attaching
  const canSave = title.trim().length > 0 && body.trim().length > 0 && !blocked

  function insertSnippet(
    current: string,
    snippet: string,
    start: number,
    end: number
  ): { next: string; cursor: number } {
    const before = current.slice(0, start)
    const after = current.slice(end)
    const prefix = before.length > 0 && !before.endsWith('\n') ? '\n' : ''
    const suffix = after.length > 0 && !after.startsWith('\n') ? '\n' : ''
    const next = `${before}${prefix}${snippet}${suffix}${after}`
    return { next, cursor: (before + prefix + snippet + suffix).length }
  }

  async function attachAtCursor(
    files: File[],
    upload: (file: File) => Promise<string>,
    failed: string
  ): Promise<void> {
    if (files.length === 0) return
    const field = bodyField.current
    const start = field?.selectionStart ?? body.length
    const end = field?.selectionEnd ?? body.length
    setAttaching(true)
    setStatus(null)
    try {
      const snippets: string[] = []
      for (const file of files) {
        snippets.push(await upload(file))
      }
      const current = field?.value ?? body
      const inserted = insertSnippet(
        current,
        snippets.join('\n'),
        Math.min(start, current.length),
        Math.min(end, current.length)
      )
      setBody(inserted.next)
      requestAnimationFrame(() => {
        field?.focus()
        field?.setSelectionRange(inserted.cursor, inserted.cursor)
      })
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught, failed) })
    } finally {
      setAttaching(false)
    }
  }

  async function attachImages(files: File[]): Promise<void> {
    await attachAtCursor(
      files,
      async file => {
        const image = await uploadUpdateImage(file)
        const alt = image.alt.replace(/[[\]]/g, '').trim() || 'Image'
        return `![${alt}](${image.url})`
      },
      'The image could not be uploaded.'
    )
  }

  async function attachFiles(files: File[]): Promise<void> {
    await attachAtCursor(
      files,
      async file => {
        const stored = await uploadUpdateFile(file)
        const label = stored.filename.replace(/[[\]]/g, '').trim() || 'file'
        return `[${label}](${stored.url})`
      },
      'The file could not be uploaded.'
    )
  }

  function startEdit(update: LeagueUpdate): void {
    setEditing(update)
    setTitle(update.title)
    setBody(update.body)
    setStatus(null)
    requestAnimationFrame(() => {
      document.getElementById('update-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  function cancelEdit(): void {
    setEditing(null)
    setTitle('')
    setBody('')
  }

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setBusy(true)
    setStatus(null)
    try {
      await action()
      setStatus({ tone: 'success', text: success })
      homepage.reload()
      archive.reload()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setBusy(false)
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    const nextTitle = title.trim()
    const nextBody = body.trim()
    if (editing) {
      await run(async () => {
        await editLeagueUpdate(editing.id, { title: nextTitle, body: nextBody })
        cancelEdit()
      }, 'Update saved.')
      return
    }
    await run(async () => {
      await publishLeagueUpdate({ title: nextTitle, body: nextBody })
      setTitle('')
      setBody('')
    }, 'Update is live on the homepage.')
  }

  return (
    <div className="stack">
      {status && <Notice tone={status.tone}>{status.text}</Notice>}

      {homepage.loading ? (
        <Card title="Homepage update">
          <Loading />
        </Card>
      ) : homepage.data ? (
        <Card
          title="On the homepage"
          subtitle={formatPostedDate(homepage.data.postedAt, homepage.data.editedAt)}
          actions={
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={blocked}
                onClick={() => startEdit(homepage.data!)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn"
                disabled={blocked}
                onClick={() =>
                  void run(() => removeHomepageUpdate(), 'Update removed from the homepage.')
                }
              >
                Remove from homepage
              </button>
            </div>
          }
        >
          <h3 className="update-admin__title">{homepage.data.title}</h3>
          <UpdateBody text={homepage.data.body} />
        </Card>
      ) : (
        <Card title="Homepage update">
          {homepage.error ? (
            <Notice tone="error">{homepage.error}</Notice>
          ) : (
            <EmptyState title="Nothing on the homepage">
              Post below and it appears under the buttons on the front page. Taking it down later
              keeps it on the{' '}
              <Link className="player__name--link" to="/updates">
                updates
              </Link>{' '}
              archive.
            </EmptyState>
          )}
        </Card>
      )}

      <div id="update-editor">
      <Card
        title={editing ? 'Edit update' : 'Post an update'}
        subtitle={
          editing
            ? `Originally posted ${formatDate(editing.postedAt)}. Saving keeps that date and records when it was edited.`
            : 'The newest post replaces whatever is on the front page. Older posts stay in the archive.'
        }
      >
        <form className="stack" onSubmit={event => void submit(event)}>
          <label className="field">
            <span className="field__label">Title</span>
            <input
              className="input"
              value={title}
              maxLength={140}
              placeholder="Season 34 signups are open"
              onChange={event => setTitle(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Body</span>
            <textarea
              ref={bodyField}
              className="input"
              rows={6}
              maxLength={5000}
              value={body}
              placeholder="What the league needs to know."
              onChange={event => setBody(event.target.value)}
              onPaste={event => {
                const files = Array.from(event.clipboardData.files).filter(file =>
                  file.type.startsWith('image/')
                )
                if (files.length === 0) return
                event.preventDefault()
                void attachImages(files)
              }}
            />
            <span className="field__hint">
              A line that starts with - becomes a bullet. Pasted http(s) URLs become links.
              Images show as thumbnails; attached files show as downloads.
            </span>
          </label>
          <div className="row">
            <label className="btn">
              {attaching ? 'Uploading…' : 'Add image'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                disabled={blocked}
                onChange={event => {
                  const files = Array.from(event.target.files ?? [])
                  event.target.value = ''
                  void attachImages(files)
                }}
                style={{ display: 'none' }}
              />
            </label>
            <label className="btn">
              {attaching ? 'Uploading…' : 'Add file'}
              <input
                type="file"
                multiple
                disabled={blocked}
                onChange={event => {
                  const files = Array.from(event.target.files ?? [])
                  event.target.value = ''
                  void attachFiles(files)
                }}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {body.trim() && (
            <div className="update-admin__preview">
              <span className="field__label">Preview</span>
              <UpdateBody text={body} />
            </div>
          )}
          <div className="row">
            <button type="submit" className="btn btn--primary" disabled={!canSave}>
              {busy ? (editing ? 'Saving…' : 'Posting…') : editing ? 'Save edits' : 'Post update'}
            </button>
            {editing && (
              <button type="button" className="btn" disabled={blocked} onClick={cancelEdit}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </Card>
      </div>

      <Card title="Archive" subtitle="Newest first, including posts taken off the homepage" flush>
        {archive.loading ? (
          <Loading />
        ) : archive.data && archive.data.length > 0 ? (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Home</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {archive.data.map(update => (
                  <tr key={update.id}>
                    <td>{formatPostedDate(update.postedAt, update.editedAt)}</td>
                    <td>{update.title}</td>
                    <td>{update.onHomepage ? 'Live' : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={blocked}
                        onClick={() => startEdit(update)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No updates posted yet" />
        )}
      </Card>
    </div>
  )
}
